import { supabase } from './supabase/client';

// Savings Account interfaces
export interface SavingsAccount {
  id: string;
  student_id: string;
  name: string;
  description?: string;
  target_amount?: number;
  current_amount: number;
  auto_deposit_percentage: number;
  auto_deposit_threshold: number;
  is_active: boolean;
  is_protected: boolean;
  goal_date?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateSavingsAccountDTO {
  student_id: string;
  name: string;
  description?: string;
  target_amount?: number;
  auto_deposit_percentage?: number;
  auto_deposit_threshold?: number;
  is_protected?: boolean;
  goal_date?: Date;
}

export interface UpdateSavingsAccountDTO {
  id: string;
  name?: string;
  description?: string;
  target_amount?: number;
  auto_deposit_percentage?: number;
  auto_deposit_threshold?: number;
  is_active?: boolean;
  is_protected?: boolean;
  goal_date?: Date;
}

export interface TransferFundsDTO {
  fromAccountId: string;
  toAccountId?: string; // If null, transfer to main balance
  amount: number;
  studentId: string;
  reason?: string;
}

const TABLE_NAME = 'savings_accounts';

export const savingsAccountService = {
  /**
   * Create a new savings account for a student
   */
  async createSavingsAccount(dto: CreateSavingsAccountDTO): Promise<SavingsAccount> {
    try {
      // Validate auto deposit percentage
      if (dto.auto_deposit_percentage && (dto.auto_deposit_percentage < 0 || dto.auto_deposit_percentage > 100)) {
        throw new Error('Auto deposit percentage must be between 0 and 100');
      }

      // Validate student exists
      const { data: student, error: studentError } = await supabase
        .from('students')
        .select('id')
        .eq('id', dto.student_id)
        .single();

      if (studentError || !student) {
        throw new Error(studentError?.message || 'Student not found');
      }

      // Check if student already has max accounts (5)
      const { count, error: countError } = await supabase
        .from(TABLE_NAME)
        .select('id', { count: 'exact' })
        .eq('student_id', dto.student_id)
        .eq('is_active', true);

      if (countError) throw countError;
      if (count && count >= 5) {
        throw new Error('Maximum number of savings accounts (5) reached');
      }

      const accountData = {
        student_id: dto.student_id,
        name: dto.name,
        description: dto.description,
        target_amount: dto.target_amount,
        current_amount: 0, // Always start with zero
        auto_deposit_percentage: dto.auto_deposit_percentage || 0,
        auto_deposit_threshold: dto.auto_deposit_threshold || 0,
        is_active: true,
        is_protected: dto.is_protected || false,
        goal_date: dto.goal_date?.toISOString(),
      };

      const { data, error } = await supabase.from(TABLE_NAME).insert(accountData).select().single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[SavingsAccountService.createSavingsAccount] Error:', error);
      throw error;
    }
  },

  /**
   * Get all savings accounts for a student
   */
  async getStudentSavingsAccounts(studentId: string): Promise<SavingsAccount[]> {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('student_id', studentId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('[SavingsAccountService.getStudentSavingsAccounts] Error:', error);
      throw error;
    }
  },

  /**
   * Get a specific savings account
   */
  async getSavingsAccount(accountId: string, studentId: string): Promise<SavingsAccount> {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('id', accountId)
        .eq('student_id', studentId) // Ensure student owns the account
        .single();

      if (error) throw error;
      if (!data) throw new Error('Savings account not found');
      return data;
    } catch (error) {
      console.error('[SavingsAccountService.getSavingsAccount] Error:', error);
      throw error;
    }
  },

  /**
   * Update a savings account
   */
  async updateSavingsAccount(dto: UpdateSavingsAccountDTO): Promise<SavingsAccount> {
    try {
      const updateData: any = {
        updated_at: new Date().toISOString(),
      };

      if (dto.name !== undefined) updateData.name = dto.name;
      if (dto.description !== undefined) updateData.description = dto.description;
      if (dto.target_amount !== undefined) updateData.target_amount = dto.target_amount;
      if (dto.is_active !== undefined) updateData.is_active = dto.is_active;
      if (dto.is_protected !== undefined) updateData.is_protected = dto.is_protected;
      if (dto.goal_date !== undefined) updateData.goal_date = dto.goal_date?.toISOString();

      if (dto.auto_deposit_percentage !== undefined) {
        if (dto.auto_deposit_percentage < 0 || dto.auto_deposit_percentage > 100) {
          throw new Error('Auto deposit percentage must be between 0 and 100');
        }
        updateData.auto_deposit_percentage = dto.auto_deposit_percentage;
      }

      if (dto.auto_deposit_threshold !== undefined) {
        if (dto.auto_deposit_threshold < 0) {
          throw new Error('Auto deposit threshold must be non-negative');
        }
        updateData.auto_deposit_threshold = dto.auto_deposit_threshold;
      }

      const { data, error } = await supabase.from(TABLE_NAME).update(updateData).eq('id', dto.id).select().single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[SavingsAccountService.updateSavingsAccount] Error:', error);
      throw error;
    }
  },

  /**
   * Delete a savings account (if not protected and balance is zero)
   */
  async deleteSavingsAccount(accountId: string, studentId: string): Promise<void> {
    try {
      // Get the account first to check conditions
      const account = await this.getSavingsAccount(accountId, studentId);

      if (account.is_protected) {
        throw new Error('Cannot delete a protected savings account');
      }

      if (account.current_amount > 0) {
        throw new Error('Cannot delete a savings account with a positive balance. Transfer funds first.');
      }

      const { error } = await supabase.from(TABLE_NAME).delete().eq('id', accountId).eq('student_id', studentId);

      if (error) throw error;
    } catch (error) {
      console.error('[SavingsAccountService.deleteSavingsAccount] Error:', error);
      throw error;
    }
  },

  /**
   * Transfer funds between savings accounts or to main balance
   */
  async transferFunds(dto: TransferFundsDTO): Promise<void> {
    // One database call moves both sides together, so a failure can never
    // take money out of one account without putting it in the other.
    const { error } = await supabase.rpc('transfer_funds', {
      p_student_id: dto.studentId,
      p_from: dto.fromAccountId,
      p_to: dto.toAccountId || 'balance',
      p_amount: dto.amount,
    });
    if (error) {
      console.error('[SavingsAccountService.transferFunds] Error:', error);
      throw new Error(error.message);
    }
  },

  /**
   * Get savings summary for a student
   */
  async getSavingsSummary(studentId: string): Promise<{
    totalSaved: number;
    totalGoals: number;
    activeAccounts: number;
    goalProgress: Array<{
      accountId: string;
      accountName: string;
      currentAmount: number;
      targetAmount?: number;
      progressPercentage: number;
      goalDate?: string;
      isOnTrack: boolean;
    }>;
  }> {
    try {
      const accounts = await this.getStudentSavingsAccounts(studentId);

      const activeAccounts = accounts.filter((a) => a.is_active);
      const totalSaved = activeAccounts.reduce((sum, a) => sum + a.current_amount, 0);
      const totalGoals = activeAccounts.filter((a) => a.target_amount).length;

      const goalProgress = activeAccounts
        .filter((a) => a.target_amount)
        .map((account) => {
          const progressPercentage = account.target_amount
            ? Math.min((account.current_amount / account.target_amount) * 100, 100)
            : 0;

          let isOnTrack = true;
          if (account.goal_date && account.target_amount) {
            const daysRemaining = Math.ceil(
              (new Date(account.goal_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
            );
            const requiredDailyAmount =
              daysRemaining > 0 ? (account.target_amount - account.current_amount) / daysRemaining : 0;
            isOnTrack = daysRemaining > 0 && requiredDailyAmount <= 1; // Arbitrary threshold
          }

          return {
            accountId: account.id,
            accountName: account.name,
            currentAmount: account.current_amount,
            targetAmount: account.target_amount,
            progressPercentage,
            goalDate: account.goal_date,
            isOnTrack,
          };
        });

      return {
        totalSaved,
        totalGoals,
        activeAccounts: activeAccounts.length,
        goalProgress,
      };
    } catch (error) {
      console.error('[SavingsAccountService.getSavingsSummary] Error:', error);
      throw error;
    }
  },

  /**
   * Get savings account templates for common goals
   */
  getSavingsAccountTemplates(): Array<{
    name: string;
    description: string;
    targetAmount: number;
    autoDepositPercentage: number;
    isProtected: boolean;
    goalMonths: number;
  }> {
    return [
      {
        name: 'Emergency Fund',
        description: 'Build an emergency fund for unexpected expenses',
        targetAmount: 50,
        autoDepositPercentage: 10,
        isProtected: true,
        goalMonths: 6,
      },
      {
        name: 'College Savings',
        description: 'Save for future college expenses',
        targetAmount: 500,
        autoDepositPercentage: 15,
        isProtected: true,
        goalMonths: 24,
      },
      {
        name: 'Special Purchase',
        description: 'Save for a special item or experience',
        targetAmount: 100,
        autoDepositPercentage: 20,
        isProtected: false,
        goalMonths: 3,
      },
      {
        name: 'Charity Fund',
        description: 'Set aside money for charitable giving',
        targetAmount: 25,
        autoDepositPercentage: 5,
        isProtected: false,
        goalMonths: 12,
      },
      {
        name: 'Investment Practice',
        description: 'Practice investment concepts with virtual portfolio',
        targetAmount: 200,
        autoDepositPercentage: 25,
        isProtected: false,
        goalMonths: 8,
      },
    ];
  },

  /**
   * Validate savings account data
   */
  validateSavingsAccountData(data: CreateSavingsAccountDTO | UpdateSavingsAccountDTO): void {
    if ('name' in data && data.name && data.name.length > 100) {
      throw new Error('Account name cannot exceed 100 characters');
    }

    if ('target_amount' in data && data.target_amount !== undefined && data.target_amount < 0) {
      throw new Error('Target amount cannot be negative');
    }

    if ('auto_deposit_percentage' in data && data.auto_deposit_percentage !== undefined) {
      if (data.auto_deposit_percentage < 0 || data.auto_deposit_percentage > 100) {
        throw new Error('Auto deposit percentage must be between 0 and 100');
      }
    }

    if ('goal_date' in data && data.goal_date && data.goal_date <= new Date()) {
      throw new Error('Goal date must be in the future');
    }
  },
};
